"use client";

import * as React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, KeyRound, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const trustIndicators = [{
  label: "Zero-knowledge encryption"
}, {
  label: "Dark web monitoring"
}];
const checklistFeatures = [{
  text: "Password autofill and generation"
}, {
  text: "Secure password sharing"
}, {
  text: "Dark web monitoring alerts"
}];
export default function Dashlane() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="dashlanefeaturecard" data-nav="dark" className="relative overflow-hidden bg-[hsl(221,40%,4%)] py-20 text-white lg:py-32">
      {/* Background Decorative Numeral */}
      <div aria-hidden="true" className="pointer-events-none absolute top-4 left-4 -z-0 select-none font-mono text-[200px] font-bold leading-none text-white/5 md:top-8 md:left-12 md:text-[280px]">
        06
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* Left Column: Context & Trust */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-4 w-1 bg-primary" aria-hidden="true" />
              <span className="font-mono text-4xl font-semibold tracking-wider text-primary uppercase sm:text-5xl lg:text-6xl">
                06 // Dashlane
              </span>
            </div>

            <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Password security made effortless
            </h2>

            <p className="mt-6 text-base leading-relaxed text-white/75 sm:text-lg">
              Dashlane streamlines your digital life with seamless autofill,
              military-grade credential generation, and encrypted vaults built to
              keep your accounts shielded from credential leaks.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {trustIndicators.map((item, i) => <div key={item.label} data-index={i} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-white/90">
                  <ShieldCheck className="size-4 text-primary shrink-0" />
                  <span>{item.label}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Promotional Featured Card */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <div className="relative">
              {/* L-shaped corner accent brackets in primary color */}
              <div aria-hidden="true" className="pointer-events-none absolute -top-2 -left-2 size-4 border-t-2 border-l-2 border-primary z-20" />
              <div aria-hidden="true" className="pointer-events-none absolute -top-2 -right-2 size-4 border-t-2 border-r-2 border-primary z-20" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-2 -left-2 size-4 border-b-2 border-l-2 border-primary z-20" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-2 -right-2 size-4 border-b-2 border-r-2 border-primary z-20" />

              <Card className="relative rounded-lg border border-[hsl(221,30%,15%)] bg-[hsl(221,30%,8%)] p-6 shadow-2xl md:p-12">
                {/* Top Status Row */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex size-14 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <KeyRound className="size-7" />
                  </div>
                  <Badge variant="outline" className="border-primary/20 bg-primary/10 text-xs font-semibold tracking-wider text-primary uppercase">
                    Best for password management
                  </Badge>
                </div>

                {/* Card Title & Description */}
                <div className="mt-8">
                  <h3 className="text-2xl font-bold tracking-tight text-white md:text-3xl">
                    Smart Password Manager
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/70 md:text-base">
                    Keep critical logins safeguarded in zero-knowledge architecture
                    with rapid synchronization across all your browsers and mobile
                    devices.
                  </p>
                </div>

                <Separator className="my-8 bg-white/10" />

                {/* Features Checklist */}
                <ul className="space-y-4">
                  {checklistFeatures.map((feature, i) => <li key={feature.text} data-index={i} className="flex items-center gap-3 text-sm text-white/90 md:text-base">
                      <CheckCircle2 className="size-5 shrink-0 text-primary" />
                      <span>{feature.text}</span>
                    </li>)}
                </ul>

                {/* CTA Action */}
                <div className="mt-10">
                  <Button asChild size="lg" className="h-14 w-full rounded-md bg-primary text-base font-semibold text-primary-foreground shadow-md transition-colors hover:bg-primary/90">
                    <Link to="https://vpngenix.com/go/DashLane" newTab className="inline-flex w-full items-center justify-center gap-2">
                      <span>Visit Site</span>
                      <ArrowRight className="size-5 shrink-0" />
                    </Link>
                  </Button>
                </div>
              </Card>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}